import Splide from "@splidejs/splide";
import {sliderTranslations} from "../translations/sliderTranslations";

export function initSliders() {
    const defaultConfig = {
        type: 'loop',
        perPage: 4,
        perMove: 1,
        focus: 0,
        trimSpace: true,
        omitEnd: true,
        gap: '16px',
        padding: 0,
        classes: {
            page: 'splide__pagination__page slider__pagination-page',
        },
        breakpoints: {
            1199: {perPage: 3},
            767: {
                gap: '8px',
                perPage: 1,
                padding: {left: 0, right: '25%'},
            },
        },
    };

    const sliderConfigs = {
        'hero-banner-slider': {
            type: 'slide',
            perPage: 1,
            breakpoints: {1199: {perPage: 1}},
        },
        'text-card-slider': {
            perPage: 3,
            breakpoints: {
                1439: {padding: {left: '97px', right: 0}},
                1199: {perPage: 2, padding: {left: 0, right: '17%'}},
                767: {gap: '8px', perPage: 1, padding: {left: 0, right: '34%'}},
            },
        },
        'framed-card-slider': {
            perPage: 1,
            breakpoints: {
                1199: {perPage: 2, padding: 0},
                767: {gap: '8px', perPage: 1, padding: {left: 0, right: '15%'}},
            },
        },
        'card-slider-with-social-media': {
            perPage: 3,
            breakpoints: {
                1199: {perPage: 2, padding: 0},
                767: {gap: '16px', perPage: 1, padding: 0},
            },
        },
        'card-slider-with-customer-feedback': {
            perPage: 2,
            breakpoints: {
                1199: {perPage: 2},
                767: {perPage: 1, padding: 0},
            },
        },
        'card-slider-2-slides': {
            type: 'slide',
            perPage: 2,
            arrows: false,
            padding: 0,
            breakpoints: {
                1199: {arrows: false, perPage: 2, padding: 0},
                767: {arrows: true, pagination: true, gap: '8px', perPage: 1, padding: {left: 0, right: '14%'}},
            },
        },
        'row--is-slider-on-mobile': {
            perPage: 1,
            mediaQuery: "min",
            gap: '8px',
            padding: {left: 0, right: '39.5%'},
            breakpoints: {
                768: {arrows: false, destroy: true}
            }
        },
    };

    const getStyleValue = (el, prop) => parseFloat(getComputedStyle(el).getPropertyValue(prop).trim());

    const getContentWidth = (el) => {
        const style = getComputedStyle(el);
        return el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    };

    const getMaxCardBodyHeight = (sliderRoot) => {
        let maxHeight = 0;
        const cardBodies = sliderRoot.querySelectorAll('.slider__slide .card__body');

        cardBodies.forEach((el) => {
            const original = {display: el.style.display, visibility: el.style.visibility};
            el.style.display = 'flex';
            el.style.visibility = 'hidden';
            maxHeight = Math.max(maxHeight, el.offsetHeight);
            Object.assign(el.style, original);
        });

        document.documentElement.style.setProperty('--cta-slider-body-max-height', `${maxHeight}px`);
        return maxHeight;
    };

    const updatePagination = (el, data, currentPage) => {
        if (!data) return;
        const pagination = el.querySelector('.slider__pagination');
        if (pagination) {
            pagination.removeAttribute("role");
            pagination.removeAttribute("aria-label");
            pagination.innerHTML = `<span>${currentPage}/${data.items.length}</span>`;
        }
    };

    const updateArrowClasses = (slider) => {
        const arrows = slider.Components?.Arrows?.arrows;
        if (!arrows) return;
        if (arrows.prev) {
            if (slider.options.type === "slide") {
                const isAtStart = slider.index === 0;
                arrows.prev.classList.toggle('btn--disabled', isAtStart);
            } else {
                arrows.prev.classList.remove('btn--disabled');
            }
        }
        if (arrows.next) {
            if (slider.options.type === "slide") {
                const isAtEnd = slider.index === (slider.length - 1);
                arrows.next.classList.toggle('btn--disabled', isAtEnd);
            } else {
                arrows.next.classList.remove('btn--disabled');
            }
        }
    };

    const setCTASliderWidth = (el) => {
        const fixed = getStyleValue(el, '--slide-width');
        const active = getStyleValue(el, '--active-cta-slide-size');
        const gap = getStyleValue(el, '--slide-gap');
        const perPage = parseInt(getStyleValue(el, '--per-page'), 10);
        el.style.width = `${(fixed * (perPage - 1)) + (gap * (perPage - 1)) + active}px`;
    };

    const sliders = document.querySelectorAll('.slider');

    const initializedSliders = [];
    sliders.forEach((el) => {
        if (!el.classList.contains('is-initialized')) {
            let sliderPaddingLeft = 0,
                sliderPaddingRight = 0,
                specificConfig = {},
                mergedConfig = {},
                translation,
                handlingOverflow = false;

            const customConfig = el.dataset.config ? JSON.parse(el.dataset.config) : {};

            const section = el.closest("section");
            if (section) {
                section.classList.add("section--overflow-x-hidden");
            }

            for (const key in sliderConfigs) {
                if (el.classList.contains(key)) {
                    specificConfig = sliderConfigs[key];
                }
            }

            // Special case: CTA slider
            if (el.classList.contains('cta-slider')) {
                setCTASliderWidth(el);
                const perPage = parseInt(getStyleValue(el, '--per-page'), 10);
                const gap = `${getStyleValue(el, '--slide-gap')}px`;

                specificConfig = {
                    perPage,
                    gap,
                    trimSpace: false,
                    focus: 'center',
                    breakpoints: {
                        1199: {perPage},
                        767: {gap, perPage, padding: {left: '25%', right: '25%'}},
                    },
                };
            }

            try {
                translation = el.dataset.translation ? JSON.parse(el.dataset.translation) : null;
            } catch (e) {
                console.warn('Invalid translation JSON in data attribute:', e);
            }

            if (el.querySelector(".card > .card__link")) {
                specificConfig.focusableNodes = `
                    a:not([tabindex="-1"]):not(.card__link a),
                    button:not([tabindex="-1"]):not(.card__link button),
                    input:not([tabindex="-1"]):not(.card__link input),
                    textarea:not([tabindex="-1"]):not(.card__link textarea),
                    select:not([tabindex="-1"]):not(.card__link select),
                    details:not([tabindex="-1"]):not(.card__link details),
                    [tabindex]:not([tabindex="-1"]):not(.card__link [tabindex="-1"])
                `;
            }

            mergedConfig = {
                ...defaultConfig,
                ...specificConfig,
                ...customConfig,
                i18n: translation || sliderTranslations,
            };

            const slider = new Splide(el, mergedConfig);

            // CTA logic
            if (el.classList.contains('cta-slider')) {
                slider.on('ready', () => {
                    const width = window.innerWidth < 768 ? getContentWidth(el.parentNode) : el.clientWidth;
                    el.style.setProperty('--slider-width', `${width}px`);
                });

                slider.on('active', ({slide}) => {
                    const activeWidth = getComputedStyle(el).getPropertyValue('--active-cta-slide-size').trim();
                    slide.style.setProperty('--slide-width', activeWidth);
                });

                slider.on('moved', () => {
                    slider.refresh();
                });

                slider.on('move', (index, prev) => {
                    const slides = slider.Components.Slides;
                    const lastIndex = slider.length - 1;
                    const rootStyles = getComputedStyle(el);

                    const fixedWidth = rootStyles.getPropertyValue('--slide-width');
                    const activeWidth = rootStyles.getPropertyValue('--active-cta-slide-size');

                    const prevSlide = slides.getAt(prev)?.slide;
                    const destSlide = slides.getAt(index)?.slide;

                    if (!prevSlide || !destSlide) return;

                    prevSlide.style.setProperty('--slide-width', fixedWidth);
                    destSlide.style.setProperty('--slide-width', activeWidth);

                    let hiddenIndex, shownIndex;

                    if ((index > prev && index - prev === 1) || (prev === lastIndex && index === 0)) {
                        hiddenIndex = prev - 1;
                        shownIndex = index + 1;
                    } else if ((index < prev) || (prev === 0 && index === lastIndex)) {
                        hiddenIndex = prev + 1;
                        shownIndex = index - 1;
                    }

                    const hiddenSlide = slides.getAt(hiddenIndex)?.slide;
                    const shownSlide = slides.getAt(shownIndex)?.slide;

                    if (hiddenSlide) hiddenSlide.style.visibility = 'hidden';
                    if (shownSlide) shownSlide.style.visibility = 'visible';

                    const prevLink = prevSlide.querySelector('.card > .card__link');
                    const destLink = destSlide.querySelector('.card > .card__link');

                    if (prevLink) prevLink.setAttribute('tabIndex', '-1');
                    if (destLink) destLink.removeAttribute('tabIndex');

                    prevSlide.setAttribute('inert', 'true');
                    destSlide.removeAttribute('inert');

                    // Opacity animation
                    prevSlide.querySelector('.card__body')?.style && (prevSlide.querySelector('.card__body').style.opacity = 0);
                    setTimeout(() => {
                        const destBody = destSlide.querySelector('.card__body');
                        if (destBody) destBody.style.opacity = 1;
                    }, 300);
                });

                slider.on('mounted', () => {
                    setTimeout(() => {
                        const maxHeight = getMaxCardBodyHeight(slider.root);
                        const activeSlide = slider.Components.Elements.slides[slider.index];
                        slider.Components.Slides.forEach(s => {
                            s.slide.querySelector('.card__body').style.height = `${maxHeight}px`;
                            s.slide.querySelector('.card__link')?.setAttribute("tabIndex", "-1");
                            s.slide.setAttribute("inert", "true");
                        });
                        activeSlide.querySelector('.card__link')?.removeAttribute("tabIndex");
                        activeSlide.removeAttribute("inert");
                    }, 0);
                });

                slider.on('resized', () => {
                    setCTASliderWidth(el);

                    const width = window.innerWidth < 768 ? getContentWidth(el.parentNode) : el.clientWidth;
                    el.style.setProperty('--slider-width', `${width}px`);

                    const activeWidth = getComputedStyle(el).getPropertyValue('--active-cta-slide-size');
                    slider.Components.Slides.getAt(slider.index).slide.style.setProperty('--slide-width', activeWidth);

                    const maxHeight = getMaxCardBodyHeight(slider.root);
                    slider.Components.Slides.forEach(s => {
                        s.slide.querySelector('.card__body').style.height = `${maxHeight}px`;
                    });
                });
            }

            function getCurrentConfig(prop) {
                const width = window.innerWidth;
                const breakpoints = Object.keys(mergedConfig.breakpoints)
                    .map(Number)
                    .sort((a, b) => a - b);

                let obtainedProp = mergedConfig[prop];

                for (const bp of breakpoints) {
                    if (width <= bp) {
                        if (mergedConfig.breakpoints[bp][prop] !== undefined) {
                            obtainedProp = mergedConfig.breakpoints[bp][prop];
                        }
                        break;
                    }
                }

                return obtainedProp;
            }

            function handleOverflowChange(isOverflow) {
                if (handlingOverflow) return;
                handlingOverflow = true;

                const patch = {
                    type: isOverflow ? getCurrentConfig("type") : 'slide',
                    perPage: getCurrentConfig("perPage"),
                    gap: getCurrentConfig("gap"),
                    padding: isOverflow ? getCurrentConfig("padding") : 0,
                    arrows: isOverflow,
                    pagination: isOverflow,
                    drag: isOverflow,
                };

                if (slider.options.type === 'loop') {
                    slider.go(0);
                    patch.clones = isOverflow ? undefined : 0;
                }

                slider.options = {...mergedConfig, ...patch};

                slider.root.classList.toggle("slider--has-navi", isOverflow);

                handlingOverflow = false;
            }

            slider.on('move', (index) => {
                const destSlide = slider.Components.Slides.getAt(index).slide;

                if (slider.options.type === 'loop') {
                    destSlide.classList.add("is-showing");
                }
            });

            // Common lifecycle handlers
            slider.on('moved', (newIndex) => {
                if (slider.options.type === 'loop') {
                    slider.Components.Slides.getAt(newIndex).slide.classList.remove("is-showing")
                } else {
                    const isAtEnd = slider.index === (slider.length - 1);

                    slider.options = {
                        ...slider.options,
                        padding: {
                            left: isAtEnd ? sliderPaddingRight : sliderPaddingLeft,
                            right: isAtEnd ? '0px' : sliderPaddingRight
                        },
                    };

                    slider.refresh();
                }
            });

            slider.on('overflow', (isOverflow) => {
                const isMobileSlider = el.classList.contains("row--is-slider-on-mobile");
                if (isMobileSlider) {
                    if (window.innerWidth < 768) {
                        handleOverflowChange(isOverflow);
                    }
                } else {
                    handleOverflowChange(isOverflow);
                }
            });

            slider.on('mounted resize', () => {
                if (el.classList.contains('hero-banner-slider')) {
                    document.dispatchEvent(new CustomEvent('heroBannerIsMounted'));
                }
                const root = slider.root;

                if (root.querySelector(".card:not(.card--no-shadow):not(.card--outlined):not(.card--height-auto)")) {
                    root.classList.add("slider--has-card");
                }

                sliderPaddingLeft = slider?.options.padding?.left;
                sliderPaddingRight = slider?.options.padding?.right;

                const isOverflow = slider.length > getCurrentConfig("perPage");
                handleOverflowChange(isOverflow);
            });

            slider.on('mounted move updated', () => updateArrowClasses(slider));

            slider.on('pagination:mounted', (data, item) => {
                updatePagination(el, data, item.page + 1);
            });

            slider.on('pagination:updated', (data, prev, curr) => {
                updatePagination(el, data, curr.page + 1);
            });

            slider.on('destroy', function () {
                el.querySelectorAll('button, a').forEach(elmt => {
                    elmt.removeAttribute('tabindex');
                });
            });

            slider.mount();

            // save reference to resize later
            initializedSliders.push({el, slider});
        }
    });

    window.addEventListener('resize', () => {
        let sliderPaddingLeft;
        let sliderPaddingRight;
        initializedSliders.forEach(({el, slider}) => {
            if (el.classList.contains('cta-slider')) {
                setCTASliderWidth(el);
                const perPage = parseInt(getStyleValue(el, '--per-page'), 10);
                const gap = `${getStyleValue(el, '--slide-gap')}px`;
                slider.options.perPage = perPage;
                slider.options.gap = gap;

                const width = window.innerWidth < 768 ? getContentWidth(el.parentNode) : el.clientWidth;
                el.style.setProperty('--slider-width', `${width}px`);

                const activeWidth = getComputedStyle(el).getPropertyValue('--active-cta-slide-size');
                slider.Components.Slides.getAt(slider.index).slide.style.setProperty('--slide-width', activeWidth);

                const maxHeight = getMaxCardBodyHeight(slider.root);
                slider.Components.Slides.forEach(s => {
                    s.slide.querySelector('.card__body').style.height = `${maxHeight}px`;
                });
            }

            const isMobileSlider = el.classList.contains("row--is-slider-on-mobile");
            if (isMobileSlider) {
                if (!slider.state.is(Splide.STATES.IDLE)) {
                    slider.on('pagination:mounted', (data, item) => {
                        updatePagination(el, data, item.page + 1);
                    });
                    slider.on('pagination:updated', (data, prev, curr) => {
                        updatePagination(el, data, curr.page + 1);
                    });
                    slider.on('mounted move updated', () => updateArrowClasses(slider));
                }

                if (window.innerWidth >= 768) {
                    if (slider) {
                        el.querySelectorAll('button, a').forEach(elmt => {
                            elmt.removeAttribute('tabindex');
                        });
                    }
                }
            }

            sliderPaddingLeft = slider?.options.padding?.left;
            sliderPaddingRight = slider?.options.padding?.right;

            if (el.classList.contains("card-slider-2-slides")) {
                const isAtEnd = slider.index === (slider.length - 1);

                slider.options = {
                    ...slider.options,
                    padding: {
                        left: isAtEnd ? sliderPaddingLeft : 0,
                        right: isAtEnd ? 0 : sliderPaddingRight
                    },
                };

                slider.refresh();
            }
        });
    });
}